import React from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { Sun, Moon, Search, Menu, LogIn, User, LogOut, Car } from "lucide-react";
import { useTheme } from "@/context/ThemeContext";
import { useAuth } from "@/context/AuthContext";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from "@/components/ui/dropdown-menu";

const items = [
  { to: "/cars", label: "Cars", testId: "nav-cars-link" },
  { to: "/brands", label: "Brands", testId: "nav-brands-link" },
  { to: "/compare", label: "Compare", testId: "nav-compare-link" },
  { to: "/reviews", label: "Reviews", testId: "nav-reviews-link" },
  { to: "/news?category=Buyer%20Guide", label: "Buying Guide", testId: "nav-guide-link" },
  { to: "/cars?fuel=EV", label: "EV Zone", testId: "nav-ev-link" },
  { to: "/news", label: "News", testId: "nav-news-link" },
  { to: "/tools", label: "Tools", testId: "nav-tools-link" },
];

export default function Navbar() {
  const { theme, toggle } = useTheme();
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [q, setQ] = React.useState("");
  const [open, setOpen] = React.useState(false);
  const onSearch = (e) => { e.preventDefault(); if (q.trim()) navigate(`/cars?q=${encodeURIComponent(q.trim())}`); };

  return (
    <header data-testid="site-header" className="sticky top-0 z-50 bg-white/90 dark:bg-background/85 backdrop-blur-xl border-b border-[#E5E7EB]">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 h-16 flex items-center gap-6">
        {/* Logo */}
        <Link to="/" data-testid="nav-home-link" className="flex items-center gap-2 shrink-0">
          <div className="w-9 h-9 rounded-2xl bg-gradient-to-br from-[#2563EB] to-[#14B8A6] grid place-items-center text-white shadow-md">
            <Car className="w-4 h-4" />
          </div>
          <span className="font-display font-bold tracking-tight text-lg">TrendingNewCar</span>
        </Link>

        {/* Primary nav (centred) */}
        <nav className="hidden lg:flex items-center gap-1 mx-auto">
          {items.map((it) => (
            <NavLink key={it.to} to={it.to} data-testid={it.testId}
              className={({ isActive }) =>
                `px-3.5 py-2 rounded-lg text-sm font-medium transition-colors ${
                  isActive
                    ? "text-[#2563EB]"
                    : "text-[#1F2937] dark:text-foreground/80 hover:text-[#2563EB]"
                }`}>
              {it.label}
            </NavLink>
          ))}
        </nav>

        {/* Right-aligned group: search + theme + sign-in */}
        <div className="ml-auto lg:ml-0 flex items-center gap-3">
          <form onSubmit={onSearch} className="hidden md:flex items-center">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <input data-testid="nav-search-input" value={q} onChange={(e) => setQ(e.target.value)}
                placeholder="Search cars, brands…"
                className="h-10 w-48 lg:w-56 pl-9 pr-3 rounded-full bg-[#F8FAFC] dark:bg-secondary/60 border border-[#E5E7EB] text-sm outline-none focus:border-[#2563EB] focus:bg-white placeholder:text-muted-foreground transition-colors"/>
            </div>
          </form>

          <button data-testid="theme-toggle" onClick={toggle}
            aria-label={theme === "dark" ? "Switch to light mode" : "Switch to dark mode"}
            title={theme === "dark" ? "Switch to light mode" : "Switch to dark mode"}
            className="h-10 w-10 grid place-items-center rounded-full border border-[#E5E7EB] hover:bg-[#F8FAFC] transition-colors">
            {theme === "dark" ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
          </button>

          {user ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button data-testid="user-menu-trigger"
                  className="h-10 px-3 rounded-full border border-[#E5E7EB] hover:bg-[#F8FAFC] text-sm flex items-center gap-2 transition-colors">
                  <User className="w-4 h-4" /><span className="hidden md:inline">{user.name?.split(" ")[0]}</span>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuItem onClick={() => navigate("/dashboard")} data-testid="menu-dashboard">Dashboard</DropdownMenuItem>
                {user.role === "admin" && (<DropdownMenuItem onClick={() => navigate("/admin")} data-testid="menu-admin">Admin</DropdownMenuItem>)}
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => { logout(); navigate("/"); }} data-testid="menu-logout">
                  <LogOut className="w-4 h-4 mr-2" /> Logout
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <Link to="/login" data-testid="nav-login-link"
              className="btn-primary h-10 px-4 text-sm font-medium grid place-items-center shrink-0">
              <span className="flex items-center gap-2"><LogIn className="w-4 h-4" /> Sign in</span>
            </Link>
          )}

          <button data-testid="mobile-menu-toggle" onClick={() => setOpen((v) => !v)}
            className="lg:hidden h-10 w-10 grid place-items-center rounded-full border border-[#E5E7EB]">
            <Menu className="w-4 h-4" />
          </button>
        </div>
      </div>

      {open && (
        <div className="lg:hidden border-t border-[#E5E7EB] bg-white dark:bg-background/95">
          <div className="px-6 py-4 grid grid-cols-2 gap-2">
            {items.map((it) => (
              <NavLink key={it.to} to={it.to} data-testid={`mob-${it.testId}`} onClick={() => setOpen(false)}
                className="px-3 py-2 rounded-lg text-sm bg-[#F8FAFC]">{it.label}</NavLink>
            ))}
          </div>
        </div>
      )}
    </header>
  );
}
